/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sendRequest } from '../../../utils/dateHelpers';
import { notifyBillingSetupChanged } from '../../../utils/setupProgress';
import { getPlanPricing } from '../../../utils/pricingV2';
import PlanStatusModal from '../../../components/Payment/PlanStatusModal';

export { getPlanPricing } from '../../../utils/pricingV2';

const CORE_FEATURES = [
  'AI-native Productivity Measurement',
  'AI Leverage Measurement',
  'Effort Categorization',
  'Effort Survival Cohort',
  'Development Feed',
  'Verifiable GitMe Certifications'
];

const ENTERPRISE_FEATURES = [
  'All GitMe Features, plus:',
  'Private cloud',
  'On-premises deployment',
  'Custom capacity',
  'Dedicated support'
];

export const formatTokens = value => {
  if (value == null) return 'Custom';
  return new Intl.NumberFormat('en-US', {
    notation: value >= 1_000_000 ? 'compact' : 'standard',
    maximumFractionDigits: 1
  }).format(Number(value));
};

const formatPrice = cents => {
  if (cents == null) return 'Custom';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(Number(cents) / 100);
};

const formatSeats = value => {
  if (value == null) return 'Custom seats';
  return `${value} ${value === 1 ? 'seat' : 'seats'}`;
};

const formatDate = value => {
  if (!value) return 'Not available';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

const Metric = ({ label, value, detail, accent = 'cyan' }) => (
  <div className="rounded-xl border border-slate-200/80 bg-slate-50/80 p-3 dark:border-slate-800 dark:bg-slate-950/60">
    <div
      className={`mb-2 h-1 w-8 rounded-full ${
        accent === 'amber'
          ? 'bg-amber-400'
          : accent === 'violet'
            ? 'bg-violet-500'
            : 'bg-cyan-500'
      }`}
    />
    <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
      {label}
    </p>
    <p className="mt-1 text-lg font-black text-slate-900 dark:text-white">
      {value}
    </p>
    {detail && <p className="text-[10px] text-slate-500">{detail}</p>}
  </div>
);

const PlanConfigurationSkeleton = () => (
  <div
    aria-label="Loading billing"
    className="min-h-screen animate-pulse bg-slate-50 px-4 py-8 dark:bg-slate-950 sm:px-6 lg:px-8"
  >
    <div className="mx-auto max-w-[1600px] space-y-8">
      <div className="space-y-3">
        <div className="h-4 w-20 rounded bg-slate-200 dark:bg-slate-800" />
        <div className="h-8 w-72 rounded bg-slate-200 dark:bg-slate-800" />
        <div className="h-4 w-96 max-w-full rounded bg-slate-200 dark:bg-slate-800" />
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, index) => (
          <div
            key={index}
            className="min-h-[640px] rounded-[1.75rem] border border-slate-200/80 bg-white p-6 dark:border-slate-800 dark:bg-slate-900"
          >
            <div className="h-8 w-36 rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="mt-8 h-8 w-28 rounded bg-slate-200 dark:bg-slate-800" />
            <div className="mt-2 h-4 w-20 rounded bg-slate-200 dark:bg-slate-800" />
            <div className="mt-8 h-11 w-full rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="mt-5 space-y-1 rounded-2xl border border-slate-200/70 p-4 dark:border-slate-800">
              <div className="h-12 rounded bg-slate-100 dark:bg-slate-800/70" />
              <div className="h-12 rounded bg-slate-100 dark:bg-slate-800/70" />
            </div>
            <div className="mt-5 h-4 w-4/5 rounded bg-slate-200 dark:bg-slate-800" />
            <div className="mt-6 space-y-4 border-t border-slate-100 pt-5 dark:border-slate-800">
              {Array.from({ length: 5 }, (_, featureIndex) => (
                <div
                  key={featureIndex}
                  className="h-4 rounded bg-slate-100 dark:bg-slate-800/70"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const MarketingMessage = ({ message }) => {
  const teamMessage = message?.match(/^For teams up to (\d+) devs$/);

  if (!teamMessage) return message || 'Flexible team plan';

  return (
    <>
      <span>For teams up to</span>
      <strong className="ml-1 text-[1.08em] font-semibold">
        {teamMessage[1]} devs
      </strong>
    </>
  );
};

const PlanCard = ({ plan, billingInterval, current, selected, onSelect }) => {
  const planId = String(plan.plan_id);
  const enterprise = planId === '4';
  const offered = enterprise && Boolean(plan.enterprise_offer);
  const paid = ['1', '2', '3'].includes(planId) || offered;
  const features = enterprise ? ENTERPRISE_FEATURES : CORE_FEATURES;
  const annual = paid && !offered && billingInterval === 'year';
  const pricing = getPlanPricing(plan, billingInterval);
  const unavailable = paid && pricing.checkout_available === false;
  return (
    <article
      className={`relative flex min-h-[640px] flex-col overflow-hidden rounded-[1.75rem] p-6 transition duration-300 hover:-translate-y-1 ${
        enterprise
          ? 'border border-indigo-700/80 bg-gradient-to-br from-slate-950 via-indigo-950 to-violet-950 shadow-[0_20px_45px_rgba(49,46,129,0.32)] hover:shadow-[0_24px_55px_rgba(76,29,149,0.42)]'
          : `border bg-white shadow-[0_12px_35px_rgba(15,23,42,0.06)] hover:shadow-[0_20px_45px_rgba(15,23,42,0.12)] dark:bg-slate-900 ${
              selected
                ? 'border-cyan-500 ring-4 ring-cyan-100 dark:ring-cyan-950'
                : current
                  ? 'border-emerald-300 dark:border-emerald-900'
                  : 'border-slate-200/80 dark:border-slate-800'
            }`
      }`}
    >
      <div
        className={`absolute inset-x-0 top-0 h-1.5 ${
          enterprise
            ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500'
            : planId === '0'
              ? 'bg-slate-300'
              : 'bg-gradient-to-r from-cyan-400 to-blue-500'
        }`}
      />
      <div className="grid content-start grid-rows-[80px_auto] gap-1">
        <div className="flex min-h-[72px] items-start">
          <p
            className={`inline-flex max-w-full items-center whitespace-normal break-words rounded-2xl border px-3 py-1.5 leading-5 shadow-[0_4px_12px_rgba(6,182,212,0.12)] ring-1 ring-white/70 ${
              enterprise
                ? 'border-violet-300/30 bg-white/10 text-violet-100 shadow-none ring-white/10'
                : planId === '0'
                  ? 'border-slate-200 bg-gradient-to-r from-slate-100 to-slate-50 text-sm font-medium text-slate-600 dark:border-slate-700 dark:from-slate-800 dark:to-slate-900 dark:text-slate-300'
                  : 'border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50 text-sm font-medium text-cyan-800 dark:border-cyan-900 dark:from-cyan-950/70 dark:to-blue-950/50 dark:text-cyan-300'
            }`}
          >
            <MarketingMessage message={plan.marketing_message} />
          </p>
        </div>
        <div className="flex min-w-0 flex-wrap content-center items-center gap-2">
          <h3
            className={`break-words text-2xl font-black tracking-tight ${
              enterprise ? 'text-white' : 'text-slate-900 dark:text-white'
            }`}
          >
            {plan.plan_name}
          </h3>
          {annual && (
            <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.1em] text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
              Save {formatPrice(pricing.savings_cents)} / year
            </span>
          )}
        </div>
      </div>

      <div className="relative mt-5 flex h-[84px] min-w-0 flex-col justify-center overflow-hidden">
        {annual && (
          <span className="mb-0.5 block w-fit text-base font-semibold leading-5 text-slate-500 line-through decoration-2 dark:text-slate-400">
            {formatPrice(plan.monthly_price_cents)}
          </span>
        )}
        <div className="flex min-w-0 items-baseline gap-1 whitespace-nowrap">
          <span
            className={`text-4xl font-medium tracking-tight ${
              enterprise ? 'text-white' : 'text-slate-950 dark:text-white'
            }`}
          >
            {formatPrice(
              paid
                ? pricing.display_monthly_price_cents
                : plan.monthly_price_cents
            )}
          </span>
          {(offered ||
            (!enterprise && Number(plan.monthly_price_cents) > 0)) && (
            <span className="text-sm font-semibold text-slate-400">
              /{' '}
              {offered && plan.enterprise_offer.billing_interval === 'year'
                ? 'year'
                : 'month'}
            </span>
          )}
        </div>
        <div className="h-5 text-sm font-bold leading-5 text-emerald-600 dark:text-emerald-300">
          {offered
            ? 'Negotiated offer · fixed terms'
            : annual
              ? '· billed annually'
              : '\u00a0'}
        </div>
      </div>

      <button
        type="button"
        disabled={current || unavailable}
        onClick={() => onSelect(plan)}
        className={`mt-3 w-full rounded-full px-4 py-2.5 text-sm font-black transition ${
          current
            ? 'cursor-default bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
            : selected
              ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-200 hover:bg-cyan-700 dark:shadow-none'
              : enterprise
                ? 'bg-violet-400 text-white shadow-lg shadow-violet-950/30 hover:bg-violet-300'
                : 'bg-slate-950 text-white hover:bg-cyan-600 dark:bg-white dark:text-slate-950 dark:hover:bg-cyan-200'
        }`}
      >
        {unavailable
          ? 'Coming soon'
          : current
            ? 'Current plan'
            : selected
              ? 'Selected'
              : offered
                ? 'Choose Enterprise'
                : enterprise
                  ? "Let's Talk"
                  : planId === '0'
                    ? 'Choose Free'
                    : 'Choose plan'}
      </button>

      <dl
        className={`mt-5 overflow-hidden rounded-2xl border px-4 ${
          enterprise
            ? 'border-white/15 bg-white/10'
            : 'border-slate-200/70 bg-slate-50/80 dark:border-slate-800 dark:bg-slate-950/70'
        }`}
      >
        <div className="flex min-h-[54px] items-center justify-between gap-4">
          <dt
            className={`text-[10px] font-bold uppercase tracking-[0.14em] ${
              enterprise ? 'text-indigo-200' : 'text-slate-400'
            }`}
          >
            Tokens / month
          </dt>
          <dd
            className={`shrink-0 text-right text-base font-black ${
              enterprise ? 'text-white' : 'text-slate-900 dark:text-white'
            }`}
          >
            {formatTokens(plan.included_tokens)}
          </dd>
        </div>
        <div
          className={`flex min-h-[54px] items-center justify-between gap-4 border-t ${
            enterprise
              ? 'border-white/15'
              : 'border-slate-200/70 dark:border-slate-800'
          }`}
        >
          <dt
            className={`text-[10px] font-bold uppercase tracking-[0.14em] ${
              enterprise ? 'text-indigo-200' : 'text-slate-400'
            }`}
          >
            Workspace
          </dt>
          <dd
            className={`text-right text-base font-black leading-5 ${
              enterprise ? 'text-white' : 'text-slate-900 dark:text-white'
            }`}
          >
            {planId === '0' ? '1 seat' : formatSeats(plan.seat_limit)}
          </dd>
        </div>
      </dl>

      <p
        className={`mt-4 min-h-8 text-xs font-semibold leading-5 ${
          enterprise ? 'text-indigo-100' : 'text-slate-500'
        }`}
      >
        {planId === '0'
          ? 'No overage · No credit card'
          : enterprise && !offered
            ? 'Custom usage and overage terms'
            : `Overage: ${formatPrice(pricing.overage_price_cents)} per started ${formatTokens(
                plan.overage_unit_tokens
              )} tokens`}
      </p>

      <ul
        className={`mt-5 flex-1 space-y-3 border-t pt-5 ${
          enterprise
            ? 'border-white/15'
            : 'border-slate-100 dark:border-slate-800'
        }`}
      >
        {features.map(feature => (
          <li
            key={feature}
            className={`grid grid-cols-[18px_minmax(0,1fr)] items-start gap-2 text-sm leading-5 ${
              enterprise
                ? 'text-indigo-50'
                : 'text-slate-600 dark:text-slate-300'
            }`}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 18,
                color: enterprise ? '#c4b5fd' : '#06b6d4',
                marginTop: '1px'
              }}
            />
            <span className="min-w-0">{feature}</span>
          </li>
        ))}
      </ul>
    </article>
  );
};

const PlanConfiguration = () => {
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [billingProfile, setBillingProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPlanId, setBusyPlanId] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedBillingInterval, setSelectedBillingInterval] = useState(null);
  const [billingInterval, setBillingInterval] = useState('year');
  const [planStatusModal, setPlanStatusModal] = useState(null);

  const loadBilling = useCallback(async () => {
    setLoading(true);
    try {
      const [plansResult, usageResult, profileResult] = await Promise.all([
        sendRequest('/billing/plans', 'GET'),
        sendRequest('/billing/usage', 'GET'),
        sendRequest('/billing/profile', 'GET')
      ]);
      const loadedPlans = plansResult?.plans || [];
      setPlans(loadedPlans);
      const annualAvailable = loadedPlans.some(
        plan =>
          ['1', '2', '3'].includes(String(plan.plan_id)) &&
          getPlanPricing(plan, 'year').checkout_available
      );
      if (!annualAvailable) setBillingInterval('month');
      setUsage(usageResult?.usage || null);
      setSubscription(profileResult?.subscription || null);
      setBillingProfile(profileResult || null);
      return profileResult || null;
    } catch (error) {
      toast.error(error.message || 'Billing details could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  const planSetupKnown = billingProfile?.setup != null;
  const planConfigured = billingProfile?.setup?.plan_configured === true;
  const resolvedCurrentPlanId = planConfigured
    ? (usage?.plan_id ?? billingProfile?.plan_id ?? subscription?.plan_id)
    : null;
  const currentPlanId =
    resolvedCurrentPlanId == null ? null : String(resolvedCurrentPlanId);
  const currentBillingInterval =
    usage?.billing_interval ||
    subscription?.billing_interval ||
    billingProfile?.billing_interval ||
    'month';
  const currentPlan = plans.find(
    plan => String(plan.plan_id) === currentPlanId
  );
  const usagePercent =
    usage?.usage_percent ??
    (usage?.included_tokens
      ? (Number(usage.used_tokens || 0) / Number(usage.included_tokens)) * 100
      : null);
  const meterWidth = Math.min(Math.max(Number(usagePercent || 0), 0), 100);
  const selectedPlan = useMemo(
    () =>
      plans.find(plan => String(plan.plan_id) === String(selectedPlanId)) ||
      null,
    [plans, selectedPlanId]
  );
  const displayPlans = [...plans].sort(
    (firstPlan, secondPlan) =>
      Number(firstPlan.plan_id) - Number(secondPlan.plan_id)
  );
  const isOverage = Number(usage?.overage_tokens || 0) > 0;

  const selectPlan = plan => {
    const planId = String(plan.plan_id);
    const selectionInterval =
      plan.enterprise_offer?.billing_interval ||
      (['1', '2', '3'].includes(planId) ? billingInterval : 'month');
    if (
      planId === currentPlanId &&
      (planId === '0' || selectionInterval === currentBillingInterval)
    ) {
      return;
    }
    if (planId === '4' && !plan.enterprise_offer) {
      window.open(
        'https://www.gitdotme.com/contact-sales',
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }
    setSelectedPlanId(planId);
    setSelectedBillingInterval(selectionInterval);
  };

  const proceedWithSelectedPlan = async () => {
    if (!selectedPlan) return;
    const planId = String(selectedPlan.plan_id);
    const checkoutBillingInterval =
      selectedPlan.enterprise_offer?.billing_interval ||
      (['1', '2', '3'].includes(planId)
        ? selectedBillingInterval || billingInterval
        : 'month');
    setBusyPlanId(planId);
    try {
      if (planId === '0') {
        const result = await sendRequest('/billing/change-plan', 'POST', {
          plan_id: planId,
          billing_interval: 'month'
        });
        setSelectedPlanId(null);
        setSelectedBillingInterval(null);
        const refreshedProfile = await loadBilling();
        notifyBillingSetupChanged(refreshedProfile);
        if (result?.effective === 'next_billing_period') {
          setPlanStatusModal({
            status: 'scheduled',
            planName: selectedPlan.plan_name
          });
        } else if (
          refreshedProfile?.setup?.plan_configured === true &&
          String(refreshedProfile?.plan_id) === planId
        ) {
          setPlanStatusModal({
            status: 'active',
            planName: selectedPlan.plan_name
          });
        }
        return;
      }
      window.location.assign(
        `/settings/card-management?plan_id=${encodeURIComponent(planId)}&billing_interval=${encodeURIComponent(checkoutBillingInterval)}${selectedPlan.enterprise_offer ? `&offer_id=${encodeURIComponent(selectedPlan.enterprise_offer.offer_id)}` : ''}`
      );
    } catch (error) {
      const code = error?.errorData?.code;
      if (code === 'CHECKOUT_REQUIRED' && planId !== '0') {
        window.location.assign(
          `/settings/card-management?plan_id=${encodeURIComponent(planId)}&billing_interval=${encodeURIComponent(checkoutBillingInterval)}${selectedPlan.enterprise_offer ? `&offer_id=${encodeURIComponent(selectedPlan.enterprise_offer.offer_id)}` : ''}`
        );
      } else if (code === 'SEAT_LIMIT_EXCEEDED') {
        toast.error(
          `This plan includes ${formatSeats(error.errorData.seat_limit)}, but your workspace uses ${formatSeats(error.errorData.used_seats)}.`
        );
      } else {
        toast.error(error.message || 'Plan could not be changed.');
      }
    } finally {
      setBusyPlanId(null);
    }
  };

  if (loading) {
    return <PlanConfigurationSkeleton />;
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 dark:bg-slate-950 sm:px-6 lg:px-8">
      <PlanStatusModal
        isOpen={Boolean(planStatusModal)}
        planName={planStatusModal?.planName || 'Selected'}
        status={planStatusModal?.status || 'active'}
        onClose={() => setPlanStatusModal(null)}
      />
      <div className="mx-auto max-w-[1600px] space-y-8">
        {planSetupKnown && !planConfigured && (
          <section className="rounded-[1.5rem] border border-amber-200 bg-amber-50 px-6 py-5 text-amber-950 shadow-sm dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-amber-700 dark:text-amber-300">
              Plan setup required
            </p>
            <h2 className="mt-2 text-xl font-black">
              Choose a plan to activate repository analysis.
            </h2>
            <p className="mt-2 text-sm leading-6 text-amber-800 dark:text-amber-200">
              Free and paid plans remain inactive until you make an explicit
              selection below.
            </p>
          </section>
        )}

        {planConfigured && usage && (
          <section className="overflow-hidden rounded-[1.5rem] border border-slate-200/80 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)] dark:border-slate-800 dark:bg-slate-900">
            <div className="grid lg:grid-cols-[0.85fr_1.5fr]">
              <div className="bg-gradient-to-br from-cyan-500 to-blue-600 p-4 text-white">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-cyan-100">
                    Active plan
                  </p>
                  <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold">
                    {formatSeats(usage.seats?.usedSeats || 0)} used
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-black tracking-tight">
                      {usage.plan_name || currentPlan?.plan_name || 'GitMe'}
                    </h2>
                    <p className="mt-1 text-xs text-cyan-50">
                      {formatDate(usage.period_start)} –{' '}
                      {formatDate(usage.period_end)}
                    </p>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex items-end justify-between gap-3">
                    <p className="text-base font-black">
                      {formatTokens(usage.used_tokens)} used
                    </p>
                    <p className="text-xs font-bold">
                      {usagePercent == null
                        ? 'Custom limit'
                        : `${usagePercent.toFixed(1)}%`}
                    </p>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-950/20 p-0.5">
                    <div
                      className={`h-full rounded-full transition-all ${
                        isOverage ? 'bg-amber-300' : 'bg-white'
                      }`}
                      style={{ width: `${meterWidth}%` }}
                    />
                  </div>
                  <p className="mt-2 text-xs text-cyan-50">
                    {isOverage
                      ? `${formatTokens(usage.overage_tokens)} tokens over plan`
                      : `${formatTokens(usage.remaining_tokens)} tokens remaining`}
                  </p>
                </div>
              </div>

              <div className="grid gap-2 p-3 sm:grid-cols-2 xl:grid-cols-4">
                <Metric
                  label="Included"
                  value={formatTokens(usage.included_tokens)}
                  detail="Monthly plan capacity"
                />
                <Metric
                  label="Consumed"
                  value={formatTokens(usage.used_tokens)}
                  detail="Current billing cycle"
                />
                <Metric
                  label={isOverage ? 'Overage' : 'Available'}
                  value={formatTokens(
                    isOverage ? usage.overage_tokens : usage.remaining_tokens
                  )}
                  detail={
                    isOverage ? 'Beyond included usage' : 'Included tokens left'
                  }
                  accent={isOverage ? 'amber' : 'cyan'}
                />
                <Metric
                  label="Paid overage"
                  value={`${usage.paid_charge_count || 0} charges`}
                  detail={`${usage.paid_units || 0} extra units · ${formatPrice(
                    usage.paid_amount_cents || 0
                  )}`}
                  accent="violet"
                />
                {usage.overage_collection_mode === 'grant_v1' && (
                  <>
                    <Metric
                      label="Additional capacity opened"
                      value={formatTokens(usage.granted_overage_tokens)}
                      detail="Available this cycle; no upfront charge"
                    />
                    <Metric
                      label="Remaining added capacity"
                      value={formatTokens(usage.grant_remaining_tokens)}
                      detail="Unused capacity expires at cycle end"
                    />
                    <Metric
                      label="Unpaid overage"
                      value={formatPrice(usage.unpaid_overage_amount_cents)}
                      detail="Based on started 1M units actually used"
                      accent="amber"
                    />
                  </>
                )}
              </div>
            </div>
            {usage.grace_ends_at && (
              <div className="border-t border-amber-200 bg-amber-50 px-6 py-4 text-sm font-semibold text-amber-900">
                Payment failed. Your grace period ends{' '}
                {formatDate(usage.grace_ends_at)}.
              </div>
            )}
          </section>
        )}

        <section>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-cyan-600 dark:text-cyan-300">
                Plans
              </p>
              <h2 className="mt-1 text-3xl font-black tracking-tight text-slate-950 dark:text-white">
                Choose the capacity that fits.
              </h2>
            </div>
            <p className="max-w-lg text-sm leading-6 text-slate-500">
              Every paid plan includes the complete analytics suite. Developer
              counts are guidance; token capacity and seats define the plan.
            </p>
          </div>
          <div className="mt-6 flex flex-col items-center gap-2 pb-1">
            <div className="flex items-center gap-3">
              <div
                className="inline-flex shrink-0 rounded-full border border-slate-200 bg-slate-100 p-1 shadow-sm dark:border-slate-700 dark:bg-slate-950"
                role="group"
                aria-label="Billing interval"
              >
                <button
                  type="button"
                  aria-pressed={billingInterval === 'month'}
                  onClick={() => {
                    setBillingInterval('month');
                    if (selectedPlanId !== '4') {
                      setSelectedPlanId(null);
                      setSelectedBillingInterval(null);
                    }
                  }}
                  className={`min-w-24 rounded-full px-5 py-2 text-sm font-black transition ${
                    billingInterval === 'month'
                      ? 'bg-slate-950 text-white shadow-sm dark:bg-white dark:text-slate-950'
                      : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  aria-label="Yearly — 2+ Months Free and $10 less overage"
                  aria-pressed={billingInterval === 'year'}
                  onClick={() => {
                    setBillingInterval('year');
                    if (selectedPlanId !== '4') {
                      setSelectedPlanId(null);
                      setSelectedBillingInterval(null);
                    }
                  }}
                  className={`min-w-24 rounded-full px-5 py-2 text-sm font-black transition ${
                    billingInterval === 'year'
                      ? 'bg-slate-950 text-white shadow-sm dark:bg-white dark:text-slate-950'
                      : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  Yearly
                </button>
              </div>
            </div>
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
              <span className="text-emerald-600 dark:text-emerald-300">
                Yearly:
              </span>{' '}
              Standard plans save 2+ months and get a lower overage rate.
              Enterprise offers keep their negotiated terms.
            </p>
          </div>
          <div className="mt-6 overflow-x-auto pb-3">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:min-w-[1360px] lg:grid-cols-5 lg:[zoom:0.9]">
              {displayPlans.map(plan => (
                <PlanCard
                  key={plan.plan_id}
                  plan={plan}
                  billingInterval={billingInterval}
                  current={
                    String(plan.plan_id) === currentPlanId &&
                    (String(plan.plan_id) === '0' ||
                      (plan.enterprise_offer?.billing_interval ||
                        billingInterval) === currentBillingInterval)
                  }
                  selected={
                    String(plan.plan_id) === String(selectedPlanId) &&
                    (String(plan.plan_id) === '0' ||
                      (plan.enterprise_offer?.billing_interval ||
                        billingInterval) === selectedBillingInterval)
                  }
                  onSelect={selectPlan}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="flex flex-col gap-4 rounded-[1.75rem] border border-slate-200/80 bg-white p-6 text-sm text-slate-600 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-black text-slate-900 dark:text-white">
              Historical analysis
            </h3>
            <p className="mt-2 max-w-3xl leading-6">
              Want insights from earlier commits? Historical analysis is a
              separate, one-time purchase, so it will not use any of your
              monthly plan tokens.
            </p>
          </div>
          <Link
            to="/settings/historical-analysis"
            className="inline-flex shrink-0 items-center justify-center rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-black text-white transition hover:bg-cyan-600"
          >
            Analyze earlier commits
          </Link>
        </section>
      </div>

      {selectedPlan && (
        <div className="sticky bottom-4 z-30 mx-auto mt-8 max-w-[1000px]">
          <div className="flex flex-col justify-between gap-4 rounded-[1.5rem] border border-cyan-300 bg-slate-950 p-4 text-white shadow-2xl shadow-cyan-950/30 sm:flex-row sm:items-center sm:px-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-300">
                Selected plan
              </p>
              <p className="mt-1 text-lg font-black">
                {selectedPlan.plan_name} ·{' '}
                {formatPrice(
                  getPlanPricing(
                    selectedPlan,
                    selectedBillingInterval || billingInterval
                  ).display_monthly_price_cents
                )}{' '}
                /{' '}
                {selectedPlan.enterprise_offer?.billing_interval === 'year'
                  ? 'year'
                  : 'month'}
                {!selectedPlan.enterprise_offer &&
                  (selectedBillingInterval || billingInterval) === 'year' &&
                  ` · ${formatPrice(
                    getPlanPricing(
                      selectedPlan,
                      selectedBillingInterval || billingInterval
                    ).checkout_amount_cents
                  )} billed annually`}{' '}
                · {formatTokens(selectedPlan.included_tokens)} tokens / month
              </p>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setSelectedPlanId(null);
                  setSelectedBillingInterval(null);
                }}
                className="rounded-xl border border-white/15 px-4 py-3 text-sm font-bold hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={Boolean(busyPlanId)}
                onClick={proceedWithSelectedPlan}
                className="rounded-xl bg-cyan-400 px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-300 disabled:opacity-60"
              >
                {busyPlanId
                  ? 'Please wait…'
                  : selectedPlan.plan_id === '0'
                    ? 'Activate Free Plan'
                    : 'Proceed to Billing & Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanConfiguration;
